import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { CartEntryDetailsViewModel, ProductTableMode } from "~/features/cart/cartV2/models/ProductTableViewModel";
import {
  buildKitSubLineItems,
  buildProductLineViewModel,
} from "~/features/cart/cartV2/utils/buildProductLineViewModel";
import {
  selectDescriptorForLineItem,
  type ResolvedProductGroup,
} from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";
import type { EntryGroup } from "~/features/cart/cart/dto/CartResponseDTO";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { ProductType, type ProductData } from "~/domains/product/types/ProductData";

export interface BuildCartEntryLinesInput {
  resolvedGroup: ResolvedProductGroup;
  mode: ProductTableMode;
  initiallyExpanded?: boolean;
  allowPriceReferenceEditing?: boolean;
  optionalPartDescriptors?: Record<number, ProductDescriptor>;
  lockedPackEntryPks?: ReadonlySet<number>;
  lineItemsByGroupNumber?: Record<number, LineItem[]>;
  entryGroupsByNumber?: Dict<EntryGroup>;
  packPhaseoutPredecessorPks?: ReadonlySet<number>;
  showCopyLine: boolean;
}

export function shouldShowCopyLine(product?: Pick<ProductData, "type"> | null): boolean {
  return product?.type !== ProductType.CONFIGURATOR;
}

export function buildCartEntryLines({
  resolvedGroup,
  mode,
  initiallyExpanded = true,
  allowPriceReferenceEditing = false,
  optionalPartDescriptors,
  lockedPackEntryPks,
  lineItemsByGroupNumber,
  entryGroupsByNumber,
  packPhaseoutPredecessorPks,
  showCopyLine,
}: BuildCartEntryLinesInput): CartEntryDetailsViewModel {
  // If pack phaseout predecessors exist, reorder lines: predecessors first, then successors
  let orderedLineItems = resolvedGroup.lineItems;
  let intraEntryChainInsertAfterLineIds: number[] | undefined;

  if (packPhaseoutPredecessorPks && packPhaseoutPredecessorPks.size > 0) {
    const predecessorLines = resolvedGroup.lineItems.filter((li) => packPhaseoutPredecessorPks.has(li.pk));
    const successorLines = resolvedGroup.lineItems.filter((li) => !packPhaseoutPredecessorPks.has(li.pk));

    if (predecessorLines.length > 0 && successorLines.length > 0) {
      orderedLineItems = [...predecessorLines, ...successorLines];
      // Chain connector goes after the last predecessor
      const lastPredecessor = predecessorLines[predecessorLines.length - 1];
      intraEntryChainInsertAfterLineIds = [lastPredecessor.id];
    }
  }

  // Compute copySourcePackEntryId AFTER reordering so it always picks the last successor
  const copySourcePackEntryId = orderedLineItems.length > 0 ? orderedLineItems[orderedLineItems.length - 1].id : null;

  // For PACK_PHASEOUT entries, only check successor lines for lock status.
  // Predecessor lines are locked by design; the entry should still be copyable
  // when at least one successor line is unlocked.
  const linesToCheckForCopy = packPhaseoutPredecessorPks
    ? orderedLineItems.filter((li) => !packPhaseoutPredecessorPks.has(li.pk))
    : orderedLineItems;

  return {
    initiallyExpanded,
    showCopyLine,
    canCopyLine:
      showCopyLine &&
      mode === "edit" &&
      copySourcePackEntryId !== null &&
      !linesToCheckForCopy.some((li) => lockedPackEntryPks?.has(li.pk)),
    copySourcePackEntryId,
    lines: orderedLineItems.map((lineItem) => {
      const descriptorSelection = selectDescriptorForLineItem(resolvedGroup, lineItem);
      const subLineItems =
        lineItemsByGroupNumber && entryGroupsByNumber
          ? buildKitSubLineItems(lineItem, lineItemsByGroupNumber, entryGroupsByNumber)
          : [];

      return buildProductLineViewModel({
        mode,
        lineItem,
        descriptor: descriptorSelection.descriptor,
        descriptorSelection,
        subLineItems,
        allowPriceReferenceEditing,
        optionalPartDescriptors,
        phaseoutLocked: lockedPackEntryPks?.has(lineItem.pk) ?? false,
      });
    }),
    intraEntryChainInsertAfterLineIds,
  };
}
